// ATLAS · BEARING — CORE of ORIENT. Is the current action still connected to the larger direction?
// Reads the ORACLE spine node of the orb's sector + the orb's current Step / NOW step.
// The verdict is the Alignment Engine's (olmps/alignment.ts via useAlignment, Aim orb = the Home number),
// so BEARING, Home, the Compass and ATLAS share one definition of aligned (Update Directive §7).
import { effectiveAims } from '../../../../olmps/oracle-spine-data';
import { SECTOR_LABELS } from '../../../../domain/olmps';
import { useAlignment } from '../../../../olmps/useAlignment';
import { ALIGNMENT_STATUS } from '../../../../olmps/alignment';
import { aimsOf, deityLabel, goalName } from '../../shared';
import { atlasOf, currentOf } from '../../../../olmps/atlas-data';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'atlas', toolId: 'atlas', orderFunction: 'orient', title: 'BEARING', deityName: 'ATLAS', agentId: 'telos' } as const;

export function useAtlasWidget(c: WidgetCtx): OrbWidgetModel {
  const { board } = useAlignment(c.userId);
  const node = c.sector && c.spine ? c.spine[c.sector] ?? null : null;
  const sectorAims = node ? effectiveAims(node) : aimsOf(null);
  const endeavour = node?.endeavour ?? null;
  const goal = goalName(c.sector, node);
  const deity = deityLabel(c.sector, node);
  const todayT = currentOf(atlasOf(node).today);

  // The NOW step that matches the orb's Step (by title); else the top-ranked one.
  const ns = (c.step ? c.steps.find((s) => s.title === c.step?.title) : null) ?? c.steps[0] ?? null;
  const stepTitle = c.step?.title ?? ns?.title ?? null;
  const stepSector = c.step?.sector ?? ns?.sector ?? null;

  // Aim the step is tied to: the sector's first Aim when the step lives in that sector, else the step's own trail.
  const sameSector = !!stepSector && stepSector === c.sector;
  const aim = (sameSector ? sectorAims[0]?.label : null) ?? ns?.trail.aim ?? null;
  const hasStep = !!stepTitle;

  if (!aim && !endeavour && !goal) return emptyModel(BASE, c, 'No active Aim');

  // Score the sector the Step lives in (falls back to the orb's sector); Home number = Aim orb.
  const scoreSector = stepSector ?? c.sector;
  const al = scoreSector ? board[scoreSector]?.aim ?? null : null;
  const status = al?.status ?? 'unset';
  const needsAttention = status === 'drifting' || status === 'misaligned' || status === 'neglected';
  const verdict = al && status !== 'unset' ? ALIGNMENT_STATUS[status].word : null;
  const scoreLabel = al && al.shown != null ? `${al.shown}` : '—';

  const lines: string[] = [];
  if (c.sectorName) lines.push(c.sectorName);
  if (endeavour) lines.push(`Endeavour: ${endeavour}${deity ? ` · ${deity}` : ''}`);
  if (goal) lines.push(`Goal: ${goal}`);
  if (aim) lines.push(`Aim: ${aim}`);
  else if (sectorAims[0]) lines.push(`Aim: ${sectorAims[0].label}`);
  if (todayT?.text) lines.push(`Today: ${todayT.text}`);
  if (stepTitle) lines.push(`Current Step: ${stepTitle}`);
  if (al && verdict) lines.push(`Alignment ${scoreLabel} · ${verdict}`);
  if (al && status !== 'unset') lines.push(al.reason);

  const sectorLabel = scoreSector ? SECTOR_LABELS[scoreSector] : c.sectorName;
  const summary =
    al && verdict ? `${sectorLabel} ${scoreLabel} · ${verdict}`
    : aim ? `Aim: ${aim}` : endeavour ? `Endeavour: ${endeavour}` : `Goal: ${goal}`;

  return {
    ...BASE,
    status: needsAttention ? 'attention' : 'quiet',
    summary: summary.length > 40 ? `${summary.slice(0, 39).trimEnd()}…` : summary,
    alert: needsAttention && al ? al.reason : null,
    lines,
    primaryAction: { label: 'Reorient', run: () => c.openTool('atlas'), tone: 'gold' },
    secondaryActions: [],
    signals: { active: hasStep || !!verdict, alerted: needsAttention, related: hasStep },
  };
}
